import { useTranslation } from 'react-i18next';
import './Statistics.css';
import React, { useRef, useContext, useEffect, useState, useMemo } from 'react';
import { AppContext, appContext } from '../../../context/AppContext';
import {
	AreaChart,
	Area,
	XAxis,
	Tooltip,
	YAxis,
	CartesianGrid,
	ResponsiveContainer,
	TooltipProps,
} from 'recharts';
import { ValueType, NameType, Payload } from 'recharts/types/component/DefaultTooltipContent';
import { CategoryViews, LocaleCode, Product, firstCharToUpper } from '@anticatrade/shared';
import { Spinner, EmptyState, Pagination, useDeferredLoading, Link } from '@anticatrade/shared';
import {
	ViewsStatisticsResponse,
	MessagesStatisticsResponse,
	ViewsTotalResponse,
	ProductsResponse,
	ViewsByCategoryResponse,
	MessagesTotalResponse,
	FollowersTotalResponse,
	FavoritesTotalResponse,
} from '@anticatrade/admin';
import SlIcon from '@shoelace-style/shoelace/dist/react/icon/index.js';
import SlTooltip from '@shoelace-style/shoelace/dist/react/tooltip/index.js';
import { StatsCounter } from '../../base/StatsCounter/StatsCounter';
import { Heading } from '../../base/Heading/Heading';

const MOST_VIEWED_PRODUCTS_LIMIT = 10;

const Statistics = () => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [viewsStatistics, setViewsStatistics] = useState<number[]>([]);
	const [viewsTotal, setViewsTotal] = useState<number>();
	const [viewsByCategory, setViewsByCategory] = useState<CategoryViews[]>();
	const [messagesStatistics, setMessagesStatistics] = useState<number[]>([]);
	const [messagesTotal, setMessagesTotal] = useState<number>();
	const [followersTotal, setFollowersTotal] = useState<number>();
	const [favoritesTotal, setFavoritesTotal] = useState<number>();

	const { t, i18n } = useTranslation();
	const { api, handleError } = useContext(appContext as React.Context<AppContext>);

	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	const { viewsData, viewsTrend, messagesData, messagesTrend } = useMemo(() => {
		if (isLoading) {
			return {
				viewsData: [],
				viewsTrend: 0,
				messagesData: [],
				messagesTrend: 0,
			};
		}
		return {
			viewsData: formatStatistics(viewsStatistics, i18n.language as LocaleCode),
			viewsTrend: computeTrend(viewsStatistics),
			messagesData: formatStatistics(messagesStatistics, i18n.language as LocaleCode),
			messagesTrend: computeTrend(messagesStatistics),
		};
	}, [isLoading]);

	useEffect(() => {
		const fetchData = async () => {
			let viewsStatisticsRes: ViewsStatisticsResponse;
			try {
				viewsStatisticsRes = await api.viewsStatistics();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setViewsStatistics(viewsStatisticsRes.statistics);

			let viewsTotalRes: ViewsTotalResponse;
			try {
				viewsTotalRes = await api.viewsTotal();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setViewsTotal(viewsTotalRes.total);

			let viewsByCategoryRes: ViewsByCategoryResponse;
			try {
				viewsByCategoryRes = await api.viewsByCategory();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setViewsByCategory(viewsByCategoryRes.viewsByCategory);

			let messagesStatisticsRes: MessagesStatisticsResponse;
			try {
				messagesStatisticsRes = await api.messagesStatistics();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setMessagesStatistics(messagesStatisticsRes.statistics);

			let messagesTotalRes: MessagesTotalResponse;
			try {
				messagesTotalRes = await api.messagesTotal();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setMessagesTotal(messagesTotalRes.total);

			let followersTotalRes: FollowersTotalResponse;
			try {
				followersTotalRes = await api.followersTotal();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setFollowersTotal(followersTotalRes.total);

			let favoritesTotalRes: FavoritesTotalResponse;
			try {
				favoritesTotalRes = await api.favoritesTotal();
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setFavoritesTotal(favoritesTotalRes.total);

			setIsLoading(false);
		};

		fetchData();
	}, []);

	return (
		<div className='statistics'>
			<Heading left={<h1 className='statistics__title'>{t('Statistics')}</h1>} />
			{showContent ? (
				<div className='statistics__content'>
					<div className='statistics__totals'>
						<div className='statistics__views-total'>
							<div className='statistics__views-total-title'>
								{t('Views')}
								<SlTooltip
									className='statistics__counter-info-tooltip'
									content={t('Views count description')}
									placement='bottom'
									distance={12}
								>
									<SlIcon
										className='statistics__counter-info-icon'
										name='info-circle'
									/>
								</SlTooltip>
							</div>
							<div className='statistics__views-total-counter'>{viewsTotal}</div>
						</div>
						<div className='statistics__messages-total'>
							<div className='statistics__messages-total-title'>
								{t('Messages')}
								<SlTooltip
									className='statistics__counter-info-tooltip'
									content={t('Messages count description')}
									placement='bottom'
									distance={12}
								>
									<SlIcon
										className='statistics__counter-info-icon'
										name='info-circle'
									/>
								</SlTooltip>
							</div>
							<div className='statistics__messages-total-counter'>
								{messagesTotal}
							</div>
						</div>
						<div className='statistics__favorites-total'>
							<div className='statistics__favorites-total-title'>
								{t('Favorites')}
								<SlTooltip
									className='statistics__counter-info-tooltip'
									content={t('Favorites count description')}
									placement='bottom'
									distance={12}
								>
									<SlIcon
										className='statistics__counter-info-icon'
										name='info-circle'
									/>
								</SlTooltip>
							</div>
							<div className='statistics__favorites-total-counter'>
								{favoritesTotal}
							</div>
						</div>
						<div className='statistics__followers-total'>
							<div className='statistics__followers-total-title'>
								{t('Followers')}
								<SlTooltip
									className='statistics__counter-info-tooltip'
									content={t('Followers count description')}
									placement='bottom'
									distance={12}
								>
									<SlIcon
										className='statistics__counter-info-icon'
										name='info-circle'
									/>
								</SlTooltip>
							</div>
							<div className='statistics__followers-total-counter'>
								{followersTotal}
							</div>
						</div>
					</div>
					<div className='statistics__views wrapped'>
						<div className='statistics__views-title'>
							{t('Views in the last 30 days')}
							{viewsTrend > 0 && (
								<span>
									{' '}
									{t('{n} in the last 3 days').replace('{n}', `+${viewsTrend}%`)}
								</span>
							)}
						</div>
						<TrendChart data={viewsData} type='views' />
					</div>
					<div className='statistics__messages wrapped'>
						<div className='statistics__messages-title'>
							{t('Messages in the last 30 days')}
							{messagesTrend > 0 && (
								<span>
									{' '}
									{t('{n} in the last 3 days').replace(
										'{n}',
										`+${messagesTrend}%`,
									)}
								</span>
							)}
						</div>
						<TrendChart data={messagesData} type='messages' />
					</div>
					<div className='statistics__most-viewed'>
						<div className='statistics__most-viewed-products-wrapper'>
							<MostViewedProducts />
						</div>
						{viewsByCategory != null && viewsByCategory.length > 0 && (
							<div className='statistics__most-viewed-categories-wrapper'>
								<MostViewedCategories viewsByCategory={viewsByCategory} />
							</div>
						)}
					</div>
				</div>
			) : (
				showSpinner && <Spinner />
			)}
		</div>
	);
};

const MostViewedProducts = () => {
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [products, setProducts] = useState<Product[] | null>();
	const [page, setPage] = useState<number>(1);
	const [count, setCount] = useState<number>(1);
	const [minHeight, setMinHeight] = useState<number>(0);

	const contentRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (contentRef.current && !isLoading) {
			const height = contentRef.current.getBoundingClientRect().height;
			setMinHeight((prev) => Math.max(prev, height));
		}
	}, [products, isLoading]);

	const { t, i18n } = useTranslation();
	const { api, handleError } = useContext(appContext as React.Context<AppContext>);

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			let productsRes: ProductsResponse;
			try {
				productsRes = await api.products(
					page,
					MOST_VIEWED_PRODUCTS_LIMIT,
					undefined,
					undefined,
					'popularity',
				);
			} catch (err) {
				handleError(err as Error);
				setIsLoading(false);
				return;
			}
			setProducts(productsRes.products);
			setCount(productsRes.count);
			setIsLoading(false);
		};

		fetchData();
	}, [page]);

	const isInitialLoad = isLoading && products == null;
	const isPageTransition = isLoading && products != null;
	const hasProducts = products != null && products.length > 0;

	const { showSpinner: showInitialSpinner, showContent: showInitialContent } =
		useDeferredLoading(isInitialLoad);

	return (
		<div className='statistics__most-viewed-products wrapped'>
			{showInitialContent ? (
				<div
					ref={contentRef}
					style={{
						minHeight,
						position: 'relative',
						display: 'flex',
						flexDirection: 'column',
					}}
				>
					{isPageTransition && (
						<div className='statistics__most-viewed-products-overlay'>
							<Spinner />
						</div>
					)}
					<div
						style={{
							flex: 1,
							display: 'flex',
							flexDirection: 'column',
							...(isPageTransition ? { opacity: 0.5 } : undefined),
						}}
					>
						<div className='statistics__most-viewed-products-title'>
							{t('Most viewed items')}
						</div>
						{!hasProducts ? (
							<EmptyState
								title={t('You have not added any item yet')}
								description={t(
									'Once you add items and get your first views you will be able to see a ranking of your most viewed items',
								)}
								icon='boxes'
							/>
						) : (
							<div className='statistics__most-viewed-products-body'>
								<div className='statistics__most-viewed-products-list'>
									{products.map((p) => {
										const isCompleted = p.status === 'completed';
										const imgSrc = isCompleted
											? `${import.meta.env.VITE_PRODUCT_IMAGE_URL}/${p.id}/thumbnail-${p.image.fileName}.webp`
											: `${import.meta.env.VITE_PRODUCT_IMAGE_URL.replace(/\/products$/, '/products-upload')}/${p.id}/main-${p.image.fileName}.${p.image.originalExtension}`;
										return (
											<div className='statistics__most-viewed-product'>
												<div className='statistics__most-viewed-product-image'>
													<Link
														key={p.id}
														path={`/products/${encodeURIComponent(p.id)}`}
													>
														<img src={imgSrc} />
													</Link>
												</div>

												<div className='statistics__most-viewed-product-title'>
													<Link
														key={p.id}
														path={`/products/${encodeURIComponent(p.id)}`}
													>
														{p.texts[i18n.language as LocaleCode].title}
													</Link>
												</div>
												<div>
													<StatsCounter
														type='views'
														count={p.views!}
														placement='left'
														distance={15}
													/>
													<StatsCounter
														type='favorites'
														count={p.favoritesCount!}
														placement='left'
														distance={15}
													/>
												</div>
											</div>
										);
									})}
								</div>
								<Pagination
									count={count}
									limit={MOST_VIEWED_PRODUCTS_LIMIT}
									page={page}
									setPage={setPage}
									t={t}
								/>
							</div>
						)}
					</div>
				</div>
			) : (
				showInitialSpinner && <Spinner />
			)}
		</div>
	);
};

interface MostViewedCategoriesProps {
	viewsByCategory: CategoryViews[];
}

const MostViewedCategories = ({ viewsByCategory }: MostViewedCategoriesProps) => {
	const { t } = useTranslation();

	return (
		<div className='statistics__most-viewed-categories wrapped'>
			<div className='statistics__most-viewed-categories-title'>
				{t('Most viewed categories')}
			</div>
			<div className='statistics__most-viewed-categories-list'>
				{viewsByCategory.map(({ name, views }) => {
					return (
						<div className='statistics__most-viewed-category'>
							<div className='statistics__most-viewed-category-name'>{name}</div>
							<StatsCounter
								type='views'
								count={views}
								placement='left'
								distance={15}
							/>
						</div>
					);
				})}
			</div>
		</div>
	);
};

interface ChartData {
	data: any[];
	type: 'views' | 'messages';
}

const TrendChart = ({ data, type }: ChartData) => {
	return (
		<ResponsiveContainer width='100%' height={250}>
			<AreaChart data={data}>
				<defs>
					<linearGradient id='colorUv' x1='0' y1='0' x2='0' y2='1'>
						<stop offset='5%' stopColor='#8884d8' stopOpacity={0.8} />
						<stop offset='95%' stopColor='#8884d8' stopOpacity={0} />
					</linearGradient>
				</defs>
				<CartesianGrid strokeDasharray='3 3' />
				<XAxis
					dataKey='time'
					interval={'preserveStartEnd'}
					minTickGap={50}
					tickMargin={7}
				/>
				<YAxis />
				<Tooltip
					content={type === 'views' ? <ViewsTrendTooltip /> : <MessagesTrendTooltip />}
				/>
				<Area
					type='monotone'
					dataKey='total'
					stroke='#8884d8'
					strokeWidth={2}
					fillOpacity={1}
					fill='url(#colorUv)'
				/>
			</AreaChart>
		</ResponsiveContainer>
	);
};

const ViewsTrendTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
	return <TrendTooltip active={active} payload={payload} type='views' />;
};

const MessagesTrendTooltip = ({ active, payload }: TooltipProps<ValueType, NameType>) => {
	return <TrendTooltip active={active} payload={payload} type='messages' />;
};

interface TrendTooltipProps {
	active: boolean | undefined;
	payload: Payload<ValueType, NameType>[] | undefined;
	type: 'views' | 'messages';
}

const TrendTooltip = ({ active, payload, type }: TrendTooltipProps) => {
	const { i18n, t } = useTranslation();

	if (!active || !payload || payload.length === 0) return null;

	const today = new Date();
	const dayOffset = payload[0].payload.__offset;
	const date = new Date();
	date.setDate(today.getDate() - dayOffset);

	const formattedDateRaw = date.toLocaleDateString(i18n.language, {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});
	const formattedDate = firstCharToUpper(formattedDateRaw);

	return (
		<div
			style={{
				background: 'var(--color-onaccent)',
				padding: '8px 12px',
				border: '1px solid var(--border-primary)',
			}}
		>
			<strong>{formattedDate}</strong>
			<div>
				{type === 'views' ? t('Views') : t('Messages')}: {payload[0].value}
			</div>
		</div>
	);
};

const formatStatistics = (statistics: number[], locale: LocaleCode): any[] => {
	const points = [];
	let dayCounter = statistics.length;
	for (const total of statistics) {
		const d = new Date();
		d.setDate(d.getDate() - dayCounter);
		points.push({
			time: `${d.toLocaleDateString(locale, {
				day: '2-digit',
				month: 'short',
			})}`,
			total: total,
			__offset: dayCounter, // used to re-compute the date in an expanded format inside the tooltip
		});
		dayCounter -= 1;
	}
	return points;
};

const computeTrend = (statistics: number[]): number => {
	if (statistics.length < 4) {
		return 0;
	}

	const last3 = statistics.slice(-3);
	const previous = statistics.slice(0, -3);

	const avgLast3 = last3.reduce((sum, val) => sum + val, 0) / last3.length;
	const avgPrevious = previous.reduce((sum, val) => sum + val, 0) / previous.length;
	if (avgPrevious === 0) {
		return 0;
	}

	const trend = ((avgLast3 - avgPrevious) / avgPrevious) * 100;
	return Math.round(trend);
};

export { Statistics };
